/**
 * Property records lookup agent.
 *
 * Given an appointment's street address, asks Claude (through the backend
 * Anthropic proxy — same routing as RoutyAgent, no browser key) to pull
 * public-record / aggregator data about the property: square footage,
 * beds/baths, year built, last sold price, etc. Claude does the research with
 * Anthropic's server-side web search tool, which executes entirely within the
 * one Messages API call, then reports what it found via a structured tool
 * call.
 *
 * This intentionally stays trade-agnostic (no assumptions about painting,
 * roofing, etc.) — it just surfaces the property facts an estimator would
 * look up by hand, and the briefing UI renders them as their own section.
 *
 * Results are cached per normalized address for the session (searches are
 * billed per use), including "nothing found" results. A lookup that errors
 * (e.g. web search not enabled on the Anthropic org) resolves to null and is
 * NOT cached as "not found" — the UI simply omits the section.
 */
import Anthropic from '@anthropic-ai/sdk';
import { config } from '../../config/config';
import { anthropicProxyFetch } from './RoutyAgent';
import type { PropertyRecords } from '../../services/aiSummaryService';

const TOOL_NAME = 'submit_property_records';
const MAX_TOKENS = 1000;
// One extra search of budget specifically so the agent can run the
// "zillow.com <address>" snippet-mining pass for the Zestimate.
const MAX_WEB_SEARCHES = 5;

const SYSTEM_PROMPT = `You are a property records researcher helping a field/sales team prepare for an on-site appointment. You are given one property address. Use web search to find what public records and property aggregators report about it.

Look for: property type (single family, condo, commercial, etc.), living-area square footage, bedrooms, bathrooms, year built, lot size, the LAST SOLD price and date, and any current estimated value. Good sources include Zillow, Redfin, Realtor.com, Trulia, county property appraiser/assessor pages, and public-records aggregators. Two or three targeted searches are usually enough — try the full address first, then variations (without the ZIP+4, with common abbreviations) if needed.

Always try to find the current estimated market value. Try Realtor.com FIRST — search "realtor.com" plus the address; its property pages (and often the search snippet itself) carry an estimated value and are usually accessible. If Realtor.com yields nothing, fall back to Zillow's Zestimate or the Redfin Estimate: those sites often block direct page access, but the estimate frequently appears in the SEARCH RESULT SNIPPET itself — read the snippets carefully before giving up. Label the value with its source, e.g. "$412,000 (Realtor.com estimate)", "$410,000 (Zestimate)", or "$405,500 (Redfin Estimate)".

Rules:
- Report ONLY values you actually found in search results. Never estimate, interpolate, or fill in typical values. Leave any field you could not confirm unset.
- If sources disagree, prefer county/assessor data over aggregator data and mention the discrepancy in sourceNote.
- sourceNote must briefly say where the data came from and how reliable it is (e.g. "County appraiser record" vs "Aggregator compilation — may be outdated").
- If you cannot find this property at all, submit found: false with everything else unset.

When done, call ${TOOL_NAME} exactly once with your findings. Never include markdown in field values.`;

const PROPERTY_RECORDS_TOOL: Anthropic.Tool = {
  name: TOOL_NAME,
  description: 'Submit the public-record property data found for the address (or found: false when nothing was found).',
  input_schema: {
    type: 'object',
    properties: {
      found: {
        type: 'boolean',
        description: 'True when at least one concrete property fact was found for this specific address.',
      },
      propertyType: { type: 'string', description: 'e.g. "Single Family Residence", "Condo", "Commercial".' },
      squareFeet: { type: 'number', description: 'Living-area square footage.' },
      bedrooms: { type: 'number' },
      bathrooms: { type: 'number', description: 'May be fractional, e.g. 2.5.' },
      yearBuilt: { type: 'number' },
      lotSize: { type: 'string', description: 'Lot size as reported, e.g. "0.29 acres" or "12,632 sq ft".' },
      lastSoldPrice: { type: 'string', description: 'Last sold amount as reported, e.g. "$285,000".' },
      lastSoldDate: { type: 'string', description: 'When it last sold, as reported, e.g. "March 2019".' },
      estimatedValue: { type: 'string', description: 'Current estimated value if a source reports one, e.g. "$410,000 (Zestimate)".' },
      additionalNotes: {
        type: 'string',
        description: 'One short sentence of other estimate-relevant facts found (stories, pool, garage, recent renovation), if any.',
      },
      sourceNote: {
        type: 'string',
        description: 'Where the data came from and its reliability, e.g. "Marion County appraiser + Zillow — aggregator data, verify on site."',
      },
    },
    required: ['found'],
  },
};

/**
 * Session cache keyed by normalized address. Stores the in-flight promise so
 * concurrent callers (e.g. a summary and a sales prep racing) share one
 * lookup instead of paying for two rounds of web searches.
 */
const lookupCache = new Map<string, Promise<PropertyRecords | null>>();

function normalizeAddress(address: string): string {
  return address.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Look up public property records for an address. Resolves to the structured
 * findings (`found: false` when the search ran but nothing matched), or null
 * when the lookup itself failed — callers should treat null as "no section",
 * not "no records".
 *
 * Never throws; safe to run in parallel with the briefing generation.
 */
export function lookupPropertyRecords(address: string): Promise<PropertyRecords | null> {
  const key = normalizeAddress(address);
  if (!key) return Promise.resolve(null);

  const cached = lookupCache.get(key);
  if (cached) return cached;

  const promise = runLookup(address).catch((error) => {
    console.warn('Property records lookup failed:', error);
    // Failed lookups are evicted so a later generation can retry.
    lookupCache.delete(key);
    return null;
  });
  lookupCache.set(key, promise);
  return promise;
}

async function runLookup(address: string): Promise<PropertyRecords | null> {
  const client = new Anthropic({
    // Dummy key: the proxy strips/ignores it and injects the real key
    // server-side. `dangerouslyAllowBrowser` is safe — no key in the bundle.
    baseURL: `${config.api.baseUrl}/api/anthropic-proxy`,
    apiKey: 'routemize-proxy',
    dangerouslyAllowBrowser: true,
    fetch: anthropicProxyFetch,
  });

  const response = await client.messages.create({
    model: config.anthropic.model,
    max_tokens: MAX_TOKENS,
    system: SYSTEM_PROMPT,
    tools: [
      // Anthropic server-side web search: executed by Anthropic within this
      // one API call, so it works through the backend proxy unchanged.
      {
        type: 'web_search_20250305',
        name: 'web_search',
        max_uses: MAX_WEB_SEARCHES,
      },
      PROPERTY_RECORDS_TOOL,
    ],
    messages: [
      {
        role: 'user',
        content: `Find public property records for this address:\n\n${address}`,
      },
    ],
  });

  const toolUseBlock = response.content.find(
    (block): block is Anthropic.Messages.ToolUseBlock =>
      block.type === 'tool_use' && block.name === TOOL_NAME
  );
  if (!toolUseBlock) {
    // The model answered in prose instead of the structured hand-off —
    // treat as a failed lookup rather than fabricating a "not found".
    return null;
  }

  const parsed = toolUseBlock.input as PropertyRecords;
  return {
    found: !!parsed.found,
    propertyType: parsed.propertyType || undefined,
    squareFeet: parsed.squareFeet || undefined,
    bedrooms: parsed.bedrooms || undefined,
    bathrooms: parsed.bathrooms || undefined,
    yearBuilt: parsed.yearBuilt || undefined,
    lotSize: parsed.lotSize || undefined,
    lastSoldPrice: parsed.lastSoldPrice || undefined,
    lastSoldDate: parsed.lastSoldDate || undefined,
    estimatedValue: parsed.estimatedValue || undefined,
    additionalNotes: parsed.additionalNotes || undefined,
    sourceNote: parsed.sourceNote || undefined,
  };
}
